import React, { useState, useCallback } from 'react';
import { useUserSession } from '../contexts/UserContext.js';
import { usePendingOperations } from '../contexts/PendingOperationsContext.js';
import { useAuth0 } from '@auth0/auth0-react';
import { isDevelopment } from '../utils/environment.js';
import FomoAlert from './FomoAlert.js';
import './Feedback.css';
import back from '../Ui/backWhite.png';

const Feedback = ({ goBack }) => {
  const { userSession } = useUserSession();
  const { trackOperation, requestNavigation } = usePendingOperations();
  const { getAccessTokenSilently } = useAuth0();
  
  // Development bypass pattern for testing
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const allowDevBypass = isLocalhost && isDevelopment();
  
  // Check if user has access (paid and session loaded) OR development bypass
  const hasAccess = (userSession.isLoaded && userSession.hasPaid) || allowDevBypass;
  
  // FOMO alert state
  const [showFomoAlert, setShowFomoAlert] = useState(false);
  
  const [activeTab, setActiveTab] = useState('documentation');
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackType, setFeedbackType] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Wrap goBack with navigation guard
  const handleGoBack = useCallback(() => {
    requestNavigation(() => goBack());
  }, [goBack, requestNavigation]);

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackText.trim()) {
      alert('Please enter your feedback');
      return;
    }

    setIsSubmitting(true);
    
    // Track this operation so navigation is blocked until complete
    await trackOperation('submit-feedback', async () => {
      try {
        const token = await getAccessTokenSilently();
        const apiUrl = process.env.REACT_APP_API_URL || 'https://intellegacy-back.vercel.app';
        const cleanApiUrl = apiUrl.replace(/\/$/, '');
        
        const response = await fetch(`${cleanApiUrl}/feedback/submit`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            feedbackType,
            feedbackText: feedbackText.trim()
          })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.message || 'Failed to submit feedback');
        }

        if (result.success) {
          alert('Thank you for your feedback! We have received it and will review it shortly.');
          setFeedbackText('');
          setFeedbackType('general');
        } else {
          throw new Error(result.message || 'Failed to submit feedback');
        }
        
      } catch (error) {
        alert(`Failed to submit feedback: ${error.message}. Please try again.`);
      } finally {
        setIsSubmitting(false);
      }
    });
  };

  // Show loading state while session is loading
  if (!userSession.isLoaded) {
    return (
      <div className="feedback-container">
        <div className="feedback-loading">Loading...</div>
      </div>
    );
  }

  // Check access control - only paid users can access feedback
  if (!hasAccess) {
    return (
      <div className="feedback-container">
        <div className="feedback-header">
          <button className="back-button" onClick={handleGoBack}>
            <img src={back} className="back-btn-arrow"></img> Back to Dashboard
          </button>
          <h1>Help & Feedback</h1>
        </div>
        
        {/* Show disabled preview of the documentation */}
        <div className="feedback-content disabled-preview">
          <div className="tab-navigation">
            <button className="tab-button disabled" onClick={() => setShowFomoAlert(true)}>
              🔒 Overview
            </button>
            <button className="tab-button disabled" onClick={() => setShowFomoAlert(true)}>
              🔒 Documentation
            </button>
            <button className="tab-button disabled" onClick={() => setShowFomoAlert(true)}>
              🔒 Feedback
            </button>
          </div>

          <div className="tab-content">
            <div className="fomo-preview">
              <h3>📚 Comprehensive Documentation Available</h3>
              <p>Complete guides, tutorials, and help resources...</p>
              <div className="content-blur">
                <p>• Getting Started Guide</p>
                <p>• Advanced Features</p>
                <p>• API Documentation</p>
                <p>• Troubleshooting</p>
                <p>• Best Practices</p>
              </div>
              <button 
                className="preview-unlock-btn"
                onClick={() => setShowFomoAlert(true)}
              >
                🔓 Unlock Full Documentation
              </button>
            </div>
          </div>
        </div>

        <FomoAlert 
          isOpen={showFomoAlert}
          onClose={() => setShowFomoAlert(false)}
          featureName="Help & Documentation"
          customMessage="Probably want to complete registration first, then maybe you'll be allowed to access the full documentation and submit feedback. Just saying... 🤷‍♂️"
        />
      </div>
    );
  }

  return (
    <div className="feedback-container">
      <div className="feedback-header">
          <button className="back-button" onClick={handleGoBack}>
            <img src={back} className="back-btn-arrow"></img> Back to Dashboard
          </button>
        <h1>Help & Feedback</h1>
      </div>

      <div className="feedback-tabs">
        <button 
          className={`tab-button ${activeTab === 'documentation' ? 'active' : ''}`}
          onClick={() => setActiveTab('documentation')}
        >
          Documentation
        </button>
        <button 
          className={`tab-button ${activeTab === 'feedback' ? 'active' : ''}`}
          onClick={() => setActiveTab('feedback')}
        >
          Send Feedback
        </button>
      </div>

      <div className="feedback-content">
        {activeTab === 'documentation' && (
          <div className="documentation-section">
            <h2>💡 How to Use Intellegacy</h2>
              <p className='settings-description'>
                A fully comprehensive feature breakdown and manual
              </p>
            <div className="doc-section">
              <h3>🗨️ Communication</h3>
              <p>
                The Communication feature allows you to teach your AI how you speak and express yourself.
                Start conversations to build your digital personality's speech patterns and responses.
              </p>
              <ul>
                <li>Click "Communication" from the dashboard</li>
                <li>Start typing or speaking to your AI</li>
                <li>The more you interact, the better it learns your style</li>
                <li>Your conversations help build your digital consciousness</li>
              </ul>
            </div>

            <div className="doc-section">
              <h3>🧷 Manage Media</h3>
              <p>
                Upload photos, videos, documents, and other media to create your life's digital library.
                This helps preserve your memories and experiences for your digital self.
              </p>
              <ul>
                <li>Upload photos from important life events</li>
                <li>Add videos of yourself speaking or doing activities</li>
                <li>Include documents that represent your thoughts and ideas</li>
                <li>All media helps build a comprehensive digital you</li>
              </ul>
            </div>

            <div className="doc-section">
              <h3>🎙️ Voice Replication</h3>
              <p>
                Clone your voice to ensure your digital self sounds just like you.
                Upload voice samples to train the AI on your unique speech patterns.
              </p>
              <ul>
                <li>Record yourself speaking in different tones</li>
                <li>Include various emotions and expressions</li>
                <li>The AI will learn to replicate your voice accurately</li>
                <li>Your digital self will sound authentically like you</li>
              </ul>
            </div>

            <div className="doc-section">
              <h3>🧠 Model Preferences</h3>
              <p>
                Customize your personal settings and how your AI behaves.
                Set preferences for responses, personality traits, and interaction styles.
              </p>
              <ul>
                <li>Adjust personality sliders (formal/casual, serious/humorous)</li>
                <li>Set preferred topics and interests</li>
                <li>Configure response length and detail level</li>
                <li>Fine-tune your digital personality</li>
              </ul>
            </div>

            <div className="doc-section">
              <h3>📄 Manage and Organize</h3>
              <p>
                Store and maintain the latest version of your legal will and important documents.
                Ensure your digital legacy is properly documented and accessible.
              </p>
              <ul>
                <li>Upload your legal will document</li>
                <li>Add beneficiary information</li>
                <li>Include instructions for your digital assets</li>
                <li>Keep documents updated and secure</li>
              </ul>
            </div>

            <div className="doc-section">
              <h3>🦔 Profile Management</h3>
              <p>
                Manage your personal information and account settings.
                Keep your profile updated with current information.
              </p>
              <ul>
                <li>Update your name and contact information</li>
                <li>Change your profile picture</li>
                <li>View your account status and referral code</li>
                <li>Manage your digital identity</li>
              </ul>
            </div>

            <div className="doc-section">
              <h3>💬 Notifications</h3>
              <p>
                Stay informed about your digital consciousness progress and important updates.
                Manage notification preferences for browser and mobile alerts.
              </p>
              <ul>
                <li>Enable browser notifications for real-time updates</li>
                <li>Set phone notification preferences</li>
                <li>Get alerts about training progress</li>
                <li>Receive important system announcements</li>
              </ul>
            </div>

            <div className="doc-section">
              <h3>🧲 Share & Refer</h3>
              <p>
                Share Intellegacy with friends and family using your personal referral link.
                Help others start their journey to digital immortality while earning rewards.
              </p>
              <ul>
                <li>Use the share button to get your referral link</li>
                <li>Share via social media, email, or messaging apps</li>
                <li>Earn legacy coins for successful referrals</li>
                <li>Help build the Intellegacy community</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'feedback' && (
          <div className="feedback-section">
            <h2>Send Us Your Feedback</h2>
            <p>
              We value your input! Help us improve Intellegacy by sharing your thoughts, 
              suggestions, or reporting any issues you encounter.
            </p>

            <form onSubmit={handleFeedbackSubmit} className="feedback-form">
              <div className="form-group">
                <label htmlFor="feedback-type">Feedback Type</label>
                <select 
                  id="feedback-type"
                  value={feedbackType}
                  onChange={(e) => setFeedbackType(e.target.value)}
                  className="form-select"
                >
                  <option value="general">General Feedback</option>
                  <option value="bug">Bug Report</option>
                  <option value="feature">Feature Request</option>
                  <option value="improvement">Improvement Suggestion</option>
                  <option value="question">Question</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="feedback-text">Your Feedback</label>
                <textarea
                  id="feedback-text"
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                  placeholder="Please share your thoughts, suggestions, or describe any issues you've encountered..."
                  className="form-textarea"
                  rows="6"
                  required
                />
              </div>

              <button 
                type="submit" 
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </form>
          </div>
        )}
      </div>
      
      <FomoAlert 
        isOpen={showFomoAlert}
        onClose={() => setShowFomoAlert(false)}
        featureName="Help & Documentation"
        customMessage="Probably want to complete registration first, then maybe you'll be allowed to access the full documentation and submit feedback. Just saying... 🤷‍♂️"
      />
    </div>
  );
};

export default Feedback;
